import { userContext } from './catalog';
import { findDirectoryUser } from './policyDirectory';
import { evaluateBoolean, parseExpression, validateExpression } from './parser';
import { columnQualifiedName } from './usePolicyAssetColumns';
import type { PolicyAsset, PolicyType, SyntheticUser } from './types';

export interface QueryTestResult {
  ok: boolean;
  passed?: boolean;
  unsupported?: boolean;
  errors: string[];
  explanation: string;
  userName: string;
}

export function evaluationContext(
  asset: PolicyAsset | undefined,
  user: SyntheticUser,
  row: Record<string, unknown> = {},
): Record<string, unknown> {
  const aliases: Record<string, unknown> = { ...row };
  asset?.columns.forEach((column) => {
    const value = row[column.name];
    aliases[column.name] = value;
    aliases[`${asset.name}.${column.name}`] = value;
    aliases[columnQualifiedName(asset, column.name)] = value;
  });
  return {
    ...aliases,
    ...userContext(user),
    'request.channel': user.channel,
    'request.operation': 'SELECT',
    'request.timestamp': new Date().toISOString(),
    'request.environment': 'prod',
    'resource.vault_id': asset?.vaultId ?? '',
    'resource.model_id': asset?.modelIds[0] ?? '',
    'resource.asset_id': asset?.id ?? '',
    'resource.classification': asset?.classification ?? '',
    'resource.connection_type': asset?.connectionType ?? '',
  };
}

export function testExpressionQuery(
  source: string,
  assets: PolicyAsset[],
  user: SyntheticUser,
): QueryTestResult {
  const trimmed = source.trim();
  if (!trimmed) {
    return { ok: false, errors: ['Write a query first.'], explanation: 'The Complex editor is empty.', userName: user.name };
  }
  const validation = validateExpression(trimmed, assets);
  if (!validation.ok) {
    return {
      ok: false,
      unsupported: validation.unsupported,
      errors: validation.errors,
      explanation: validation.unsupported ? 'This expression uses something the frontend simulator cannot run.' : 'The query did not parse.',
      userName: user.name,
    };
  }
  const parsed = parseExpression(trimmed);
  if (!parsed.ast) {
    return { ok: false, errors: parsed.issues.map((issue) => issue.message), explanation: 'The query did not parse.', userName: user.name };
  }
  if (parsed.ast.type === 'case') {
    return {
      ok: true,
      passed: true,
      errors: [],
      explanation: `This CASE is column mask SQL for ${user.name}. Query-time enforcement applies it to the selected column.`,
      userName: user.name,
    };
  }
  const asset = assets[0];
  const row = asset?.rows[0] ?? {};
  const ctx = evaluationContext(asset, user, row);
  const value = evaluateBoolean(parsed.ast, ctx);
  if (value === 'unsupported') {
    return { ok: false, unsupported: true, errors: [], explanation: 'The query is syntactically valid but this operator cannot be evaluated here.', userName: user.name };
  }
  return {
    ok: true,
    passed: value,
    errors: [],
    explanation: value
      ? `True for ${user.name} (${[user.role, user.department || user.region].filter(Boolean).join(', ') || 'directory user'}) against ${asset?.name ?? 'this scope'}.`
      : `False for ${user.name} (${[user.role, user.department || user.region].filter(Boolean).join(', ') || 'directory user'}) against ${asset?.name ?? 'this scope'}.`,
    userName: user.name,
  };
}

export function userById(id: string, users: SyntheticUser[] = []): SyntheticUser | undefined {
  if (!id) return undefined;
  return users.find((user) => user.id === id) ?? findDirectoryUser(id);
}

export function currentPolicyExpression(policy: {
  type: PolicyType;
  advancedExpression: string;
  rules: Array<{ expression?: string }>;
}): string {
  if (policy.type === 'ROW_LEVEL' || policy.type === 'COLUMN_LEVEL') {
    return policy.rules.find((rule) => rule.expression?.trim())?.expression
      || policy.advancedExpression
      || '';
  }
  return policy.advancedExpression;
}
